import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import {
  CalendarRange, CalendarClock, X, TrendingUp, TrendingDown, FileDown,
  DollarSign, Briefcase, Receipt, Users, MapPin, Package, Wrench, Clock, Wallet, Mail, Loader2, Lock, Unlock, CheckCircle2, Calculator, ChevronDown, ChevronRight,
} from "lucide-react";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { jobsApi, invoicesApi, techniciansApi, pricebookApi, jobTitlesApi, payrollApi, customersApi, timeclockApi, usersApi, estimatesApi, locationLogsApi } from "../lib/api";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import { DocDetail } from "./Estimates";
import { useAuth } from "../contexts/AuthContext";
import AccountingSection from "../components/reports/AccountingSection";
import JobProfitabilitySection from "../components/reports/JobProfitabilitySection";
import LocationLogSection from "../components/reports/LocationLogSection";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";
import { usePayrollPeriod } from "./payroll/usePayrollPeriod";
import { makeCanPayroll, buildSnapshotRows, adjustmentDollarTotal, errMsg, salaryPerPeriod } from "./payroll/payrollHelpers";
import PayrollPeriodHeader from "./payroll/PayrollPeriodHeader";
import PayrollSummaryCards from "./payroll/PayrollSummaryCards";
import PayrollEmployeeTable from "./payroll/PayrollEmployeeTable";
import PayrollReviewDashboard from "./payroll/PayrollReviewDashboard";
import PayrollDetailDrawer from "./payroll/PayrollDetailDrawer";
import AddAdjustmentModal from "./payroll/AddAdjustmentModal";
import EditPayrollTimeModal from "./payroll/EditPayrollTimeModal";
import CommissionAdjustmentModal from "./payroll/CommissionAdjustmentModal";
import CommissionSplitModal from "./payroll/CommissionSplitModal";
import CommissionsTab from "./payroll/tabs/CommissionsTab";
import PieceRateTab from "./payroll/tabs/PieceRateTab";
import PieceRateAdjustmentModal from "./payroll/PieceRateAdjustmentModal";
import AddSpiffModal from "./payroll/AddSpiffModal";
import AddHolidayPayModal from "./payroll/AddHolidayPayModal";
import PeriodAuditModal from "./payroll/PeriodAuditModal";

const TRADE_COLORS = ["#2563EB", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2", "#DB2777"];
const STATUS_COLORS = {
  Scheduled: "#60A5FA",
  Dispatched: "#818CF8",
  "En Route": "#A78BFA",
  "On Site": "#FB923C",
  "In Progress": "#F59E0B",
  Completed: "#10B981",
  Canceled: "#F87171",
  Unassigned: "#94A3B8",
};

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function money2(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pad(n) { return String(n).padStart(2, "0"); }
function fmtShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function fmtYmd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function dayKey(iso) {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return fmtYmd(d);
}
function weekKey(iso) {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  return fmtYmd(monday);
}
function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function downloadCsv(filename, headers, rows) {
  const body = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
}

// ---------- Page ----------
export default function Reports() {
  const navigate = useNavigate();
  const { user, hasModule } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [techs, setTechs] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [pricebook, setPricebook] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---- Filters from URL ----
  const fromDate = searchParams.get("from") || "";
  const toDate = searchParams.get("to") || "";
  const onTimeMin = Math.max(0, parseInt(searchParams.get("ontime") || "15", 10) || 15);
  const activeTab = searchParams.get("tab") || "overview";

  const updateParam = (next) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v === "" || v === null || v === undefined) p.delete(k);
      else p.set(k, String(v));
    }
    setSearchParams(p, { replace: true });
  };

  const applyPreset = (preset) => {
    const today = new Date();
    let f, t;
    if (preset === "this-month") { f = new Date(today.getFullYear(), today.getMonth(), 1); t = today; }
    else if (preset === "last-30") { f = new Date(today); f.setDate(f.getDate() - 29); t = today; }
    else if (preset === "this-quarter") { const q = Math.floor(today.getMonth() / 3); f = new Date(today.getFullYear(), q * 3, 1); t = today; }
    else if (preset === "ytd") { f = new Date(today.getFullYear(), 0, 1); t = today; }
    else return;
    updateParam({ from: fmtYmd(f), to: fmtYmd(t) });
  };

  // ---- Load all data ----
  const loadAll = useCallback(() => {
    return Promise.all([
      jobsApi.list(), invoicesApi.list(), techniciansApi.list(),
      pricebookApi.list(), jobTitlesApi.list(), customersApi.list(),
      usersApi.list().catch(() => []),
      estimatesApi.list().catch(() => []),
    ])
      .then(([j, i, t, p, jt, c, u, e]) => {
        setJobs(j); setInvoices(i); setTechs(t); setPricebook(p); setJobTitles(jt); setCustomers(c);
        setStaffUsers(u || []);
        setEstimates(e || []);
        setLoading(false);
      })
      .catch((err) => { console.error(err); toast.error("Failed to load reports data"); setLoading(false); });
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Refresh every report tab the instant a job is closed elsewhere (no manual reload needed).
  useEffect(() => {
    const onJobStatusChanged = () => loadAll();
    window.addEventListener("job-status-changed", onJobStatusChanged);
    return () => window.removeEventListener("job-status-changed", onJobStatusChanged);
  }, [loadAll]);

  const reloadInvoices = () => {
    invoicesApi.list().then(setInvoices).catch((err) => console.error(err));
  };

  // ---- Range math ----
  const range = useMemo(() => {
    const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
    let prevFromMs = null, prevToMs = null;
    if (fromMs !== null && toMs !== null) {
      const span = toMs - fromMs;
      prevToMs = fromMs - 1;
      prevFromMs = prevToMs - span;
    }
    const days = fromMs !== null && toMs !== null ? Math.ceil((toMs - fromMs) / 86400000) : null;
    const useWeekly = days !== null && days > 60;
    return { fromMs, toMs, prevFromMs, prevToMs, useWeekly };
  }, [fromDate, toDate]);

  const inRange = (iso) => {
    if (!iso) return false;
    if (range.fromMs === null && range.toMs === null) return true;
    const ts = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).getTime();
    if (Number.isNaN(ts)) return false;
    if (range.fromMs !== null && ts < range.fromMs) return false;
    if (range.toMs !== null && ts > range.toMs) return false;
    return true;
  };
  const inPrevRange = (iso) => {
    if (!iso || range.prevFromMs === null) return false;
    const ts = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).getTime();
    return ts >= range.prevFromMs && ts <= range.prevToMs;
  };

  // ---- Aggregates: current + prior ----
  const totals = useMemo(() => {
    let revenue = 0, revenuePrev = 0;
    let outstanding = 0, issuedSum = 0;
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (inRange(p.receivedAt)) revenue += Number(p.amount) || 0;
        if (inPrevRange(p.receivedAt)) revenuePrev += Number(p.amount) || 0;
      });
      if (inRange(inv.issuedAt)) issuedSum += Number(inv.total) || 0;
      if (inv.status !== "Voided") outstanding += Number(inv.balanceDue) || 0;
    });
    const completedJobs = jobs.filter((j) => j.status === "Completed" && inRange(j.end || j.start));
    const completedJobsPrev = jobs.filter((j) => j.status === "Completed" && inPrevRange(j.end || j.start));
    const jobsCompleted = completedJobs.length;
    const jobsCompletedPrev = completedJobsPrev.length;
    const valueIn = completedJobs.reduce((s, j) => s + (Number(j.value) || 0), 0);
    const valuePrev = completedJobsPrev.reduce((s, j) => s + (Number(j.value) || 0), 0);
    const avgTicket = jobsCompleted ? valueIn / jobsCompleted : 0;
    const avgTicketPrev = jobsCompletedPrev ? valuePrev / jobsCompletedPrev : 0;
    const activeTechIds = new Set(
      jobs.filter((j) => inRange(j.start || j.end) && j.techId).map((j) => j.techId),
    );
    const activeTechIdsPrev = new Set(
      jobs.filter((j) => inPrevRange(j.start || j.end) && j.techId).map((j) => j.techId),
    );
    return {
      revenue, revenuePrev,
      jobsCompleted, jobsCompletedPrev,
      avgTicket, avgTicketPrev,
      activeTechs: activeTechIds.size, activeTechsPrev: activeTechIdsPrev.size,
      outstanding, issuedSum,
    };
  }, [invoices, jobs, range]);

  // ---- Revenue series ----
  const revenueSeries = useMemo(() => {
    const buckets = new Map();
    const keyFn = range.useWeekly ? weekKey : dayKey;
    invoices.forEach((inv) => {
      if (inRange(inv.issuedAt)) {
        const k = keyFn(inv.issuedAt);
        const row = buckets.get(k) || { date: k, issued: 0, paid: 0, outstanding: 0 };
        row.issued += Number(inv.total) || 0;
        row.outstanding += Number(inv.balanceDue) || 0;
        buckets.set(k, row);
      }
      (inv.payments || []).forEach((p) => {
        if (inRange(p.receivedAt)) {
          const k = keyFn(p.receivedAt);
          const row = buckets.get(k) || { date: k, issued: 0, paid: 0, outstanding: 0 };
          row.paid += Number(p.amount) || 0;
          buckets.set(k, row);
        }
      });
    });
    return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [invoices, range]);

  // Prior-period ghost: align by bucket index so the comparison overlays on the same X axis.
  const revenueSeriesWithGhost = useMemo(() => {
    if (range.prevFromMs === null) return revenueSeries;
    const keyFn = range.useWeekly ? weekKey : dayKey;
    const prevBuckets = new Map();
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (inPrevRange(p.receivedAt)) {
          const k = keyFn(p.receivedAt);
          prevBuckets.set(k, (prevBuckets.get(k) || 0) + (Number(p.amount) || 0));
        }
      });
    });
    const prevSorted = Array.from(prevBuckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, v]) => v);
    return revenueSeries.map((row, idx) => ({ ...row, paidPrev: prevSorted[idx] ?? 0 }));
  }, [revenueSeries, invoices, range]);

  // ---- Jobs throughput ----
  const jobsByStatusOverTime = useMemo(() => {
    const buckets = new Map();
    const keyFn = range.useWeekly ? weekKey : dayKey;
    const statuses = ["Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled", "Unassigned"];
    jobs.forEach((j) => {
      const when = j.start || j.end;
      if (!inRange(when)) return;
      const k = keyFn(when);
      const row = buckets.get(k) || { date: k, ...Object.fromEntries(statuses.map((s) => [s, 0])) };
      row[j.status] = (row[j.status] || 0) + 1;
      buckets.set(k, row);
    });
    return Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [jobs, range]);

  const jobsByTrade = useMemo(() => {
    const tally = new Map();
    jobs.forEach((j) => {
      if (!inRange(j.start || j.end)) return;
      const trade = j.trade || "Other";
      tally.set(trade, (tally.get(trade) || 0) + 1);
    });
    return Array.from(tally.entries()).map(([name, value]) => ({ name, value }));
  }, [jobs, range]);

  // ---- Detail lists (below the charts) ----
  const techNameById = useMemo(() => {
    const m = {};
    techs.forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [techs]);

  const issuedList = useMemo(() => {
    return invoices
      .filter((inv) => inRange(inv.issuedAt))
      .map((inv) => ({
        id: inv.id,
        number: inv.number || inv.id,
        customer: inv.customer,
        customerId: inv.customerId,
        issuedAt: inv.issuedAt,
        total: Number(inv.total) || 0,
        balanceDue: Number(inv.balanceDue) || 0,
        status: inv.status,
      }))
      .sort((a, b) => (b.issuedAt || "").localeCompare(a.issuedAt || ""));
  }, [invoices, range]);

  const paidList = useMemo(() => {
    const rows = [];
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (inRange(p.receivedAt)) {
          rows.push({
            paymentId: p.id,
            invoiceId: inv.id,
            number: inv.number || inv.id,
            customer: inv.customer,
            customerId: inv.customerId,
            receivedAt: p.receivedAt,
            amount: Number(p.amount) || 0,
            method: p.method || "other",
          });
        }
      });
    });
    return rows.sort((a, b) => (b.receivedAt || "").localeCompare(a.receivedAt || ""));
  }, [invoices, range]);

  const jobsByStatusList = useMemo(() => {
    const order = ["Unassigned", "Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled"];
    // Sum every fully-Paid invoice per job so the Value column reflects the
    // revenue that actually landed against the job (not the raw job.value,
    // which may have been left at $0 when the invoice paid later).
    const paidByJob = new Map();
    (invoices || []).forEach((inv) => {
      if (inv.status !== "Paid" || !inv.jobId) return;
      paidByJob.set(inv.jobId, (paidByJob.get(inv.jobId) || 0) + (Number(inv.total) || 0));
    });
    const groups = {};
    jobs.forEach((j) => {
      const when = j.start || j.end;
      if (!inRange(when)) return;
      const st = j.status || "Other";
      const paid = paidByJob.get(j.id);
      (groups[st] = groups[st] || []).push({
        id: j.id,
        customer: j.customer,
        customerId: j.customerId,
        when,
        tech: j.tech || techNameById[j.techId] || "—",
        // Prefer paid-invoice total; fall back to the job's own value for
        // legacy rows that never invoiced.
        value: paid != null ? paid : (Number(j.value) || 0),
        status: st,
      });
    });
    Object.values(groups).forEach((arr) => arr.sort((a, b) => (b.when || "").localeCompare(a.when || "")));
    const ordered = [];
    order.forEach((s) => { if (groups[s]) { ordered.push({ status: s, jobs: groups[s] }); delete groups[s]; } });
    Object.keys(groups).forEach((s) => ordered.push({ status: s, jobs: groups[s] }));
    return ordered;
  }, [jobs, invoices, range, techNameById]);

  // ---- Unpaid invoices bucket ----
  // Any invoice with a balance still due, linked to a job or standalone.
  // Rendered as its own section between the job-status lists.
  const unpaidInvoiceRows = useMemo(() => {
    return (invoices || [])
      .filter((inv) => {
        const bal = Number(inv.balanceDue || 0);
        if (bal <= 0) return false;
        if (inv.status === "Voided") return false;
        return inRange(inv.issuedAt);
      })
      .map((inv) => ({
        id: inv.id,
        invoiceId: inv.id,
        jobId: inv.jobId || null,
        customer: inv.customer,
        customerId: inv.customerId,
        when: inv.issuedAt,
        dueAt: inv.dueAt || null,
        total: Number(inv.total) || 0,
        balanceDue: Number(inv.balanceDue) || 0,
        status: inv.status || "Unpaid",
      }))
      .sort((a, b) => (b.when || "").localeCompare(a.when || ""));
  }, [invoices, range]);

  // ---- Tech leaderboard ----
  const techPerf = useMemo(() => {
    const map = new Map();
    techs.forEach((t) => map.set(t.id, { techId: t.id, name: t.name, jobsCompleted: 0, revenue: 0, onTime: 0, onTimeMeasured: 0 }));
    jobs.forEach((j) => {
      if (j.status !== "Completed") return;
      if (!inRange(j.end || j.start)) return;
      const row = map.get(j.techId);
      if (!row) return;
      row.jobsCompleted += 1;
      row.revenue += Number(j.value) || 0;
      // Find arrival log entry
      const arrival = (j.log || []).find((l) => l.type === "arrived");
      if (arrival && j.start) {
        const aTs = new Date(arrival.time).getTime();
        const sTs = new Date(j.start).getTime();
        if (!Number.isNaN(aTs) && !Number.isNaN(sTs)) {
          row.onTimeMeasured += 1;
          if (aTs - sTs <= onTimeMin * 60 * 1000) row.onTime += 1;
        }
      }
    });
    return Array.from(map.values())
      .map((r) => ({
        ...r,
        avgTicket: r.jobsCompleted ? r.revenue / r.jobsCompleted : 0,
        onTimePct: r.onTimeMeasured ? Math.round((r.onTime / r.onTimeMeasured) * 100) : null,
      }))
      .sort((a, b) => b.revenue - a.revenue || b.jobsCompleted - a.jobsCompleted);
  }, [jobs, techs, range, onTimeMin]);

  // ---- Pricebook top sellers + low stock ----
  const catalog = useMemo(() => {
    const lineCount = new Map(); // pricebookId → {count, revenue}
    const matQty = new Map(); // pricebookId → qty used
    invoices.forEach((inv) => {
      if (!inRange(inv.issuedAt)) return;
      (inv.lineItems || []).forEach((li) => {
        if (!li.pricebookId) return;
        const pb = pricebook.find((p) => p.id === li.pricebookId);
        const total = (Number(li.qty) || 0) * (Number(li.unitPrice) || 0);
        const lc = lineCount.get(li.pricebookId) || { id: li.pricebookId, name: pb?.name || li.description, count: 0, revenue: 0, kind: pb?.kind };
        lc.count += 1;
        lc.revenue += total;
        lineCount.set(li.pricebookId, lc);
        if (pb?.kind === "material") {
          const mq = matQty.get(li.pricebookId) || { id: li.pricebookId, name: pb.name, qty: 0, unit: pb.unit };
          mq.qty += Number(li.qty) || 0;
          matQty.set(li.pricebookId, mq);
        }
      });
    });
    const topServices = Array.from(lineCount.values())
      .filter((r) => r.kind !== "material")
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
    const topMaterials = Array.from(matQty.values()).sort((a, b) => b.qty - a.qty).slice(0, 10);
    const lowStock = pricebook
      .filter((p) => p.kind === "material" && p.isActive !== false)
      .filter((p) => {
        const thresh = p.lowStockThreshold ?? 10;
        return (Number(p.stockOnHand) || 0) <= thresh;
      })
      .sort((a, b) => (a.stockOnHand || 0) - (b.stockOnHand || 0));
    return { topServices, topMaterials, lowStock };
  }, [invoices, pricebook, range]);

  const techColor = useMemo(() => {
    const map = new Map();
    techs.forEach((t, idx) => map.set((t.email || "").toLowerCase(), TRADE_COLORS[idx % TRADE_COLORS.length]));
    return map;
  }, [techs]);

  // ---- Tabs ----
  const tabs = [
    { id: "overview", label: "Revenue", icon: DollarSign },
    { id: "jobs", label: "Jobs", icon: Briefcase },
    { id: "profitability", label: "Profitability", icon: TrendingUp },
    { id: "techs", label: "Technicians", icon: Users },
    { id: "catalog", label: "Catalog", icon: Package },
    { id: "geo", label: "Geo activity", icon: MapPin },
    { id: "location-log", label: "Location Log", icon: MapPin },
    { id: "accounting", label: "Accounting", icon: Calculator },
    ...(isAdmin && hasModule("timeclock") ? [{ id: "payroll", label: "Payroll", icon: Wallet }] : []),
  ];

  usePageHeader({ title: "Reports", subtitle: "Business intelligence" });

  return (
    <>
      <div className="px-6 py-4 space-y-4">
        {/* Date-range bar */}
        <div className="bg-white rounded-xl border border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="reports-date-bar">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 pr-1.5 border-r border-slate-200 mr-1">
            <CalendarRange className="h-3.5 w-3.5 text-blue-600" /> Date range
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] font-semibold text-slate-500">From</label>
            <input
              type="date" value={fromDate} max={toDate || undefined}
              onChange={(e) => updateParam({ from: e.target.value })}
              data-testid="reports-date-from"
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            <label className="text-[11px] font-semibold text-slate-500">To</label>
            <input
              type="date" value={toDate} min={fromDate || undefined}
              onChange={(e) => updateParam({ to: e.target.value })}
              data-testid="reports-date-to"
              className="h-8 px-2 rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => updateParam({ from: "", to: "" })}
                title="Clear date range" data-testid="reports-date-clear"
                className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* On-time threshold */}
          <div className="flex items-center gap-1.5 pl-3 ml-1 border-l border-slate-200">
            <Clock className="h-3.5 w-3.5 text-slate-400" />
            <label className="text-[11px] font-semibold text-slate-500">On-time ≤</label>
            <input
              type="number" min={0} max={120} step={1}
              value={onTimeMin}
              onChange={(e) => updateParam({ ontime: e.target.value })}
              data-testid="reports-ontime-input"
              className="h-7 w-14 px-1.5 rounded-md border border-slate-200 text-[12.5px] text-right focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
            <span className="text-[11px] text-slate-500">min</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            {[
              { k: "this-month", l: "This month" },
              { k: "last-30", l: "Last 30 days" },
              { k: "this-quarter", l: "This quarter" },
              { k: "ytd", l: "YTD" },
            ].map((p) => (
              <button
                key={p.k} onClick={() => applyPreset(p.k)}
                data-testid={`reports-preset-${p.k}`}
                className="h-7 px-2 rounded-md text-[11.5px] font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700 border border-transparent hover:border-blue-200 transition-colors"
              >{p.l}</button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-3">
          <Kpi label="Revenue (paid)" value={money(totals.revenue)} prev={totals.revenuePrev} cur={totals.revenue} icon={DollarSign} accent="emerald" testid="kpi-revenue" />
          <Kpi label="Jobs completed" value={totals.jobsCompleted} prev={totals.jobsCompletedPrev} cur={totals.jobsCompleted} icon={Briefcase} accent="blue" testid="kpi-jobs" />
          <Kpi label="Avg ticket" value={money(totals.avgTicket)} prev={totals.avgTicketPrev} cur={totals.avgTicket} icon={Receipt} accent="violet" testid="kpi-avg-ticket" />
          <Kpi label="Active techs" value={totals.activeTechs} prev={totals.activeTechsPrev} cur={totals.activeTechs} icon={Users} accent="amber" testid="kpi-techs" />
        </div>

        {/* Section tabs */}
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1 w-fit">
          {tabs.map((t) => (
            <button
              key={t.id} onClick={() => updateParam({ tab: t.id === "overview" ? "" : t.id })}
              data-testid={`reports-tab-${t.id}`}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
                activeTab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 italic">Loading reports…</div>
        ) : (
          <>
            {activeTab === "overview" && (
              <RevenueSection series={revenueSeriesWithGhost} hasPrev={range.prevFromMs !== null} bucket={range.useWeekly ? "Weekly" : "Daily"} totals={totals} fromDate={fromDate} toDate={toDate} issuedList={issuedList} paidList={paidList} onOpenCustomer={(cid) => setProfileCustomerId(cid)} onOpenInvoice={(invId) => { const d = invoices.find((i) => i.id === invId); if (d) setInvoiceDoc(d); }} />
            )}
            {activeTab === "jobs" && (
              <JobsSection statusSeries={jobsByStatusOverTime} byTrade={jobsByTrade} bucket={range.useWeekly ? "Weekly" : "Daily"} fromDate={fromDate} toDate={toDate} jobsByStatus={jobsByStatusList} unpaidInvoiceRows={unpaidInvoiceRows} onOpenCustomer={(cid) => setProfileCustomerId(cid)} onOpenJob={(jid) => navigate(`/jobs?jobId=${jid}`)} onOpenInvoice={(invId) => { const d = invoices.find((i) => i.id === invId); if (d) setInvoiceDoc(d); }} />
            )}
            {activeTab === "profitability" && (
              <JobProfitabilitySection
                jobs={jobs}
                invoices={invoices}
                estimates={estimates}
                pricebook={pricebook}
                customers={customers}
                fromDate={fromDate}
                toDate={toDate}
                onOpenCustomer={(cid) => setProfileCustomerId(cid)}
              />
            )}
            {activeTab === "techs" && (
              <TechSection rows={techPerf} onTimeMin={onTimeMin} fromDate={fromDate} toDate={toDate} onOpenTech={(id) => navigate(`/technicians?id=${encodeURIComponent(id)}`)} />
            )}
            {activeTab === "catalog" && (
              <CatalogSection catalog={catalog} fromDate={fromDate} toDate={toDate} />
            )}
            {activeTab === "geo" && (
              <GeoSection techColor={techColor} techs={techs} fromDate={fromDate} toDate={toDate} />
            )}
            {activeTab === "location-log" && (
              <LocationLogSection
                techs={techs}
                staffUsers={staffUsers}
                fromDate={fromDate}
                toDate={toDate}
              />
            )}
            {activeTab === "accounting" && (
              <AccountingSection
                invoices={invoices}
                customers={customers}
                reloadInvoices={reloadInvoices}
                onOpenInvoice={(id) => navigate(`/estimates?id=${encodeURIComponent(id)}`)}
                fromDate={fromDate}
                toDate={toDate}
              />
            )}
            {activeTab === "payroll" && isAdmin && hasModule("timeclock") && (
              <PayrollSection
                techs={techs}
                staffUsers={staffUsers}
                jobs={jobs}
                invoices={invoices}
                pricebook={pricebook}
                jobTitles={jobTitles}
                fromDate={fromDate}
                toDate={toDate}
                applyPreset={applyPreset}
              />
            )}
          </>
        )}
      </div>
      <CustomerProfileOverlay customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} />
      {invoiceDoc && (
        <DocDetail
          kind="invoice"
          doc={invoiceDoc}
          customers={customers}
          jobs={jobs}
          onClose={() => setInvoiceDoc(null)}
          onUpdated={(u) => {
            setInvoiceDoc(u);
            invoicesApi.list().then(setInvoices).catch((err) => console.error(err));
          }}
          onDeleted={() => {
            setInvoiceDoc(null);
            invoicesApi.list().then(setInvoices).catch((err) => console.error(err));
          }}
          onConverted={() => setInvoiceDoc(null)}
          onOpenCustomer={(cid) => {
            setInvoiceDoc(null);
            setProfileCustomerId(cid);
          }}
        />
      )}
    </>
  );
}

// ---------- KPI card with delta ----------
function Kpi({ label, value, prev, cur, icon: Icon, accent, testid }) {
  const showDelta = typeof prev === "number" && (prev > 0 || cur > 0);
  let pct = null;
  if (showDelta) {
    if (prev === 0) pct = cur === 0 ? 0 : 100;
    else pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  }
  const up = pct !== null && pct >= 0;
  const accents = {
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700" },
    blue: { bg: "bg-blue-50", text: "text-blue-700" },
    violet: { bg: "bg-violet-50", text: "text-violet-700" },
    amber: { bg: "bg-amber-50", text: "text-amber-700" },
  };
  const a = accents[accent] || accents.blue;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${a.bg} ${a.text}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
          <div className="text-[22px] font-bold text-slate-900 leading-tight">{value}</div>
          {pct !== null ? (
            <div className={`mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
              {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {up ? "+" : ""}{pct}% vs prior period
            </div>
          ) : (
            <div className="mt-0.5 text-[11px] text-slate-400">Set a date range for trend</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Revenue section ----------
function RevenueSection({ series, hasPrev, bucket, totals, fromDate, toDate, issuedList, paidList, onOpenCustomer, onOpenInvoice }) {
  const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
  const exportCsv = () => {
    const headers = ["date", "issued", "paid", "outstanding"];
    const rows = series.map((r) => [r.date, r.issued.toFixed(2), r.paid.toFixed(2), r.outstanding.toFixed(2)]);
    downloadCsv(`revenue${stamp}.csv`, headers, rows);
    toast.success(`Exported ${rows.length} rows`);
  };
  const exportIssued = () => {
    const rows = issuedList.map((r) => [r.number, r.customer, fmtShort(r.issuedAt), r.total.toFixed(2), r.balanceDue.toFixed(2), r.status]);
    downloadCsv(`invoices-issued${stamp}.csv`, ["Invoice #", "Customer", "Issued", "Total", "Balance", "Status"], rows);
    toast.success(`Exported ${rows.length} invoices`);
  };
  const exportPaid = () => {
    const rows = paidList.map((r) => [r.number, r.customer, fmtShort(r.receivedAt), r.amount.toFixed(2), r.method]);
    downloadCsv(`payments-received${stamp}.csv`, ["Invoice #", "Customer", "Paid", "Amount", "Method"], rows);
    toast.success(`Exported ${rows.length} payments`);
  };
  const invoiceLink = (number, invId) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpenInvoice(invId); }}
      className="font-semibold text-blue-600 hover:underline"
      title="Open invoice"
    >
      #{number}
    </button>
  );
  return (
    <div className="space-y-4">
    <SectionCard title="Revenue overview" subtitle={`${bucket} buckets · Issued / Paid / Outstanding`} onExport={exportCsv} disabled={series.length === 0}>
      {series.length === 0 ? (
        <Empty text="No revenue activity in the selected range." />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Stat label="Issued in range" value={money(totals.issuedSum)} />
            <Stat label="Paid in range" value={money(totals.revenue)} />
            <Stat label="Open balance (live)" value={money(totals.outstanding)} accent="text-amber-700" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="gIssued" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#60A5FA" stopOpacity={0.5} /><stop offset="95%" stopColor="#60A5FA" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.5} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => money(v)} />
                <Tooltip formatter={(v) => money2(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="issued" stroke="#2563EB" fill="url(#gIssued)" name="Issued" />
                <Area type="monotone" dataKey="paid" stroke="#10B981" fill="url(#gPaid)" name="Paid" />
                {hasPrev && (
                  <Area type="monotone" dataKey="paidPrev" stroke="#94A3B8" strokeDasharray="4 4" fill="transparent" name="Paid (prior period)" />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListPanel
          title="Issued"
          count={issuedList.length}
          rows={issuedList}
          rowKey={(r) => r.id}
          getCustomerId={(r) => r.customerId}
          onOpenCustomer={onOpenCustomer}
          onExport={exportIssued}
          emptyText="No invoices issued in range."
          testid="revenue-issued-list"
          columns={[
            { key: "number", label: "Invoice #", render: (r) => invoiceLink(r.number, r.id) },
            { key: "customer", label: "Customer", render: (r) => <span className="text-slate-800 truncate">{r.customer}</span> },
            { key: "issuedAt", label: "Issued", thClass: "w-24", render: (r) => <span className="text-slate-500">{fmtShort(r.issuedAt)}</span> },
            { key: "total", label: "Total", thClass: "text-right w-20", tdClass: "text-right font-semibold text-slate-900", render: (r) => money2(r.total) },
            { key: "balance", label: "Balance", thClass: "text-right w-20", tdClass: "text-right", render: (r) => (r.balanceDue > 0 ? <span className="text-amber-700 font-semibold">{money2(r.balanceDue)}</span> : <span className="text-slate-400">{money2(0)}</span>) },
            { key: "status", label: "Status", thClass: "w-20", render: (r) => <Pill text={r.status} cls={INVOICE_STATUS_CLS[r.status] || "bg-slate-100 text-slate-600"} /> },
          ]}
        />
        <ListPanel
          title="Paid"
          count={paidList.length}
          rows={paidList}
          rowKey={(r) => r.paymentId}
          getCustomerId={(r) => r.customerId}
          onOpenCustomer={onOpenCustomer}
          onExport={exportPaid}
          emptyText="No payments received in range."
          testid="revenue-paid-list"
          columns={[
            { key: "number", label: "Invoice #", render: (r) => invoiceLink(r.number, r.invoiceId) },
            { key: "customer", label: "Customer", render: (r) => <span className="text-slate-800 truncate">{r.customer}</span> },
            { key: "receivedAt", label: "Paid", thClass: "w-24", render: (r) => <span className="text-slate-500">{fmtShort(r.receivedAt)}</span> },
            { key: "amount", label: "Amount", thClass: "text-right w-24", tdClass: "text-right font-semibold text-emerald-700", render: (r) => money2(r.amount) },
            { key: "method", label: "Method", thClass: "w-20", render: (r) => <Pill text={r.method} cls={METHOD_CLS[r.method] || "bg-slate-100 text-slate-600"} /> },
          ]}
        />
      </div>
    </div>
  );
}

// ---------- Jobs section ----------
function JobsSection({ statusSeries, byTrade, bucket, fromDate, toDate, jobsByStatus, unpaidInvoiceRows, onOpenCustomer, onOpenJob, onOpenInvoice }) {
  const statuses = ["Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled", "Unassigned"];
  const exportStatus = () => {
    const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
    const rows = statusSeries.map((r) => [r.date, ...statuses.map((s) => r[s] || 0)]);
    downloadCsv(`jobs-throughput${stamp}.csv`, ["date", ...statuses], rows);
    toast.success(`Exported ${rows.length} rows`);
  };
  return (
    <div className="space-y-4">
    <SectionCard title="Jobs throughput" subtitle={`${bucket} buckets · stacked by status`} onExport={exportStatus} disabled={statusSeries.length === 0}>
      {statusSeries.length === 0 && byTrade.length === 0 ? (
        <Empty text="No jobs in the selected range." />
      ) : (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {statuses.map((s) => (
                  <Bar key={s} dataKey={s} stackId="a" fill={STATUS_COLORS[s] || "#94A3B8"} name={s} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-72">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">By trade</div>
            <ResponsiveContainer width="100%" height="92%">
              <PieChart>
                <Pie data={byTrade} dataKey="value" nameKey="name" outerRadius={80} label={(e) => e.name}>
                  {byTrade.map((entry, idx) => <Cell key={entry.name} fill={TRADE_COLORS[idx % TRADE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </SectionCard>

      <div className="space-y-3" data-testid="jobs-status-lists">
        {jobsByStatus.length === 0 && (!unpaidInvoiceRows || unpaidInvoiceRows.length === 0) ? (
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-6 text-center text-[12.5px] text-slate-400 italic">
            No jobs in the selected range.
          </div>
        ) : (
          <>
            {jobsByStatus.map((g, idx) => {
              // Slot the "Unpaid" bucket between En Route and Completed —
              // fall back to appending it after Canceled if either anchor is
              // missing from the current view.
              const showUnpaidBefore = g.status === "Completed" && (unpaidInvoiceRows || []).length > 0;
              return (
                <React.Fragment key={g.status}>
                  {showUnpaidBefore && (
                    <UnpaidPanel rows={unpaidInvoiceRows} fromDate={fromDate} toDate={toDate} onOpenCustomer={onOpenCustomer} onOpenJob={onOpenJob} onOpenInvoice={onOpenInvoice} />
                  )}
                  <ListPanel
                    title={g.status}
                    titleColor={STATUS_COLORS[g.status] || "#94A3B8"}
                    count={g.jobs.length}
                    rows={g.jobs}
                    rowKey={(r) => r.id}
                    getCustomerId={(r) => r.customerId}
                    onOpenCustomer={onOpenCustomer}
                    onExport={() => {
                      const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
                      const rows = g.jobs.map((r) => [r.id, r.customer, fmtShort(r.when), r.tech, r.value.toFixed(2)]);
                      downloadCsv(`jobs-${g.status.replace(/\s+/g, "-").toLowerCase()}${stamp}.csv`, ["Job #", "Customer", "Scheduled", "Technician", "Value"], rows);
                      toast.success(`Exported ${rows.length} ${g.status} jobs`);
                    }}
                    emptyText="—"
                    testid={`jobs-status-${g.status.replace(/\s+/g, "-").toLowerCase()}`}
                    columns={[
                      {
                        key: "id",
                        label: "Job #",
                        thClass: "w-28",
                        render: (r) => (
                          <button
                            type="button"
                            data-testid={`report-open-job-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); onOpenJob && onOpenJob(r.id); }}
                            className="font-semibold text-blue-600 hover:underline"
                          >
                            {r.id}
                          </button>
                        ),
                      },
                      { key: "customer", label: "Customer", render: (r) => <span className="text-slate-800 truncate">{r.customer}</span> },
                      { key: "when", label: "Scheduled", thClass: "w-28", render: (r) => <span className="text-slate-500">{fmtShort(r.when)}</span> },
                      { key: "tech", label: "Technician", thClass: "w-40", render: (r) => <span className="text-slate-700">{r.tech}</span> },
                      { key: "value", label: "Value", thClass: "text-right w-24", tdClass: "text-right font-semibold text-slate-900", render: (r) => money2(r.value) },
                    ]}
                  />
                </React.Fragment>
              );
            })}
            {/* If no Completed section was rendered, still show the Unpaid section at the end. */}
            {(unpaidInvoiceRows || []).length > 0 && !jobsByStatus.some((g) => g.status === "Completed") && (
              <UnpaidPanel rows={unpaidInvoiceRows} fromDate={fromDate} toDate={toDate} onOpenCustomer={onOpenCustomer} onOpenJob={onOpenJob} onOpenInvoice={onOpenInvoice} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Tech section ----------
function TechSection({ rows, onTimeMin, fromDate, toDate, onOpenTech }) {
  const exportCsv = () => {
    const headers = ["Tech", "Jobs completed", "Revenue", "Avg ticket", `On-time % (≤${onTimeMin}min)`];
    const data = rows.map((r) => [r.name, r.jobsCompleted, r.revenue.toFixed(2), r.avgTicket.toFixed(2), r.onTimePct === null ? "n/a" : `${r.onTimePct}%`]);
    const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
    downloadCsv(`tech-performance${stamp}.csv`, headers, data);
    toast.success(`Exported ${data.length} rows`);
  };
  const shown = rows.filter((r) => r.jobsCompleted > 0);
  return (
    <SectionCard title="Technician performance" subtitle={`On-time threshold ≤ ${onTimeMin} min after scheduled start · click a row to open profile`} onExport={exportCsv} disabled={shown.length === 0}>
      {shown.length === 0 ? <Empty text="No completed jobs in the selected range." /> : (
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50/60 border-b border-slate-200">
            <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              <th className="px-3 py-2">Tech</th>
              <th className="px-3 py-2 w-32 text-right">Jobs completed</th>
              <th className="px-3 py-2 w-32 text-right">Revenue</th>
              <th className="px-3 py-2 w-32 text-right">Avg ticket</th>
              <th className="px-3 py-2 w-36 text-right">On-time %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((r) => (
              <tr key={r.techId} onClick={() => onOpenTech(r.techId)} data-testid={`tech-row-${r.techId}`} className="hover:bg-blue-50/40 cursor-pointer transition-colors">
                <td className="px-3 py-2.5 font-semibold text-slate-900">
                  <span className="flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5 text-blue-600" />{r.name}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-semibold">{r.jobsCompleted}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{money(r.revenue)}</td>
                <td className="px-3 py-2.5 text-right">{money(r.avgTicket)}</td>
                <td className="px-3 py-2.5 text-right">
                  {r.onTimePct === null ? <span className="text-slate-400 italic">n/a</span> : (
                    <span className={`text-[12px] font-bold px-2 py-0.5 rounded-full ${r.onTimePct >= 80 ? "bg-emerald-50 text-emerald-700" : r.onTimePct >= 50 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                      {r.onTimePct}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionCard>
  );
}

// ---------- Catalog section ----------
function CatalogSection({ catalog, fromDate, toDate }) {
  const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
  const exportServices = () => {
    const rows = catalog.topServices.map((s) => [s.id, s.name, s.count, s.revenue.toFixed(2)]);
    downloadCsv(`top-services${stamp}.csv`, ["ID", "Name", "Times sold", "Revenue"], rows);
    toast.success(`Exported ${rows.length} services`);
  };
  const exportMaterials = () => {
    const rows = catalog.topMaterials.map((m) => [m.id, m.name, m.qty, m.unit || ""]);
    downloadCsv(`top-materials${stamp}.csv`, ["ID", "Name", "Qty used", "Unit"], rows);
    toast.success(`Exported ${rows.length} materials`);
  };
  const exportLowStock = () => {
    const rows = catalog.lowStock.map((p) => [p.id, p.name, p.stockOnHand, p.lowStockThreshold ?? 10, p.unit || ""]);
    downloadCsv(`low-stock_now.csv`, ["ID", "Name", "On hand", "Threshold", "Unit"], rows);
    toast.success(`Exported ${rows.length} low-stock items`);
  };
  return (
    <div className="grid grid-cols-2 gap-4">
      <SectionCard title="Top services" subtitle="By revenue · in range" onExport={exportServices} disabled={catalog.topServices.length === 0}>
        {catalog.topServices.length === 0 ? <Empty text="No services invoiced in range." /> : (
          <ul className="divide-y divide-slate-100 text-[13px]">
            {catalog.topServices.map((s, i) => (
              <li key={s.id} className="py-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-[10.5px] font-bold text-slate-400 w-5">#{i + 1}</span>
                  <span className="font-semibold text-slate-900 truncate">{s.name}</span>
                  <span className="text-[10.5px] text-slate-400 font-mono">{s.id}</span>
                </span>
                <span className="text-right shrink-0">
                  <span className="font-bold text-emerald-700">{money(s.revenue)}</span>
                  <span className="text-[10.5px] text-slate-400 ml-2">{s.count}x</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      <SectionCard title="Top materials" subtitle="By qty used · in range" onExport={exportMaterials} disabled={catalog.topMaterials.length === 0}>
        {catalog.topMaterials.length === 0 ? <Empty text="No materials used in range." /> : (
          <ul className="divide-y divide-slate-100 text-[13px]">
            {catalog.topMaterials.map((m, i) => (
              <li key={m.id} className="py-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-[10.5px] font-bold text-slate-400 w-5">#{i + 1}</span>
                  <span className="font-semibold text-slate-900 truncate">{m.name}</span>
                  <span className="text-[10.5px] text-slate-400 font-mono">{m.id}</span>
                </span>
                <span className="text-right shrink-0 font-bold text-violet-700">
                  {Number(m.qty).toLocaleString()} <span className="text-slate-400 text-[10.5px] font-normal">{m.unit}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      <div className="col-span-2">
        <SectionCard title="Low-stock materials" subtitle="Current snapshot · date-range agnostic" onExport={exportLowStock} disabled={catalog.lowStock.length === 0}>
          {catalog.lowStock.length === 0 ? <Empty text="All materials are above threshold. 🎉" /> : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/60 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  <th className="px-3 py-2">Material</th>
                  <th className="px-3 py-2 w-28 text-right">On hand</th>
                  <th className="px-3 py-2 w-28 text-right">Threshold</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {catalog.lowStock.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-semibold text-slate-900">{p.name} <span className="text-[10.5px] font-mono text-slate-400">{p.id}</span></td>
                    <td className="px-3 py-2 text-right font-bold text-rose-600">{Number(p.stockOnHand).toFixed(0)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{p.lowStockThreshold ?? 10}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

// ---------- Geo section ----------
function GeoSection({ techColor, techs, fromDate, toDate }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    locationLogsApi.list(params)
      .then((rows) => {
        if (cancelled) return;
        const mapped = (Array.isArray(rows) ? rows : [])
          .filter((r) => r.techCoords && typeof r.techCoords.lat === "number")
          .map((r) => ({
            id: r.id,
            jobId: r.jobId,
            tech: r.userName,
            techEmail: (r.userEmail || "").toLowerCase(),
            customer: r.customerName,
            lat: r.techCoords.lat,
            lng: r.techCoords.lng,
            time: r.at,
            eventLabel: r.eventLabel || r.event,
          }));
        setPins(mapped);
      })
      .catch(() => { if (!cancelled) setPins([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fromDate, toDate]);

  // Compute center: average of pins, or fallback to Phoenix.
  const center = useMemo(() => {
    if (pins.length === 0) return [33.4484, -112.074];
    const lat = pins.reduce((s, p) => s + p.lat, 0) / pins.length;
    const lng = pins.reduce((s, p) => s + p.lng, 0) / pins.length;
    return [lat, lng];
  }, [pins]);

  return (
    <SectionCard title="Geo activity" subtitle={`${pins.length} GPS-tagged pins in range`}>
      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-400 text-sm" data-testid="geo-activity-loading">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading geo activity…
        </div>
      ) : pins.length === 0 ? (
        <Empty text="No GPS-tagged pins in the selected range. Pins appear once a tech clocks in, clocks out, or changes a job status with location enabled." />
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            {techs.map((t) => {
              const temail = (t.email || "").toLowerCase();
              const count = pins.filter((p) => p.techEmail === temail).length;
              if (count === 0) return null;
              return (
                <div key={t.id} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                  <span className="h-3 w-3 rounded-full" style={{ background: techColor.get(temail) }} />
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-slate-400">({count})</span>
                </div>
              );
            })}
          </div>
          <div className="h-[460px] rounded-lg overflow-hidden border border-slate-200" data-testid="geo-activity-map">
            <MapContainer center={center} zoom={11} className="h-full w-full">
              <TileLayer
                url={`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_MAPS_API_KEY}`}
                attribution="&copy; Stadia Maps &copy; OpenMapTiles &copy; OpenStreetMap contributors"
                maxZoom={20}
              />
              {pins.map((p) => (
                <CircleMarker
                  key={p.id}
                  center={[p.lat, p.lng]}
                  radius={8}
                  pathOptions={{ color: techColor.get(p.techEmail) || "#2563EB", fillOpacity: 0.7, weight: 2 }}
                >
                  <LeafletTooltip>
                    <div className="text-[11px]">
                      <div className="font-bold">{p.tech || "Unknown"} · {p.eventLabel}</div>
                      {p.jobId && <div>{p.jobId} · {p.customer || "—"}</div>}
                      <div className="text-slate-500">{new Date(p.time).toLocaleString()}</div>
                    </div>
                  </LeafletTooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ---------- Reusable pieces ----------
function SectionCard({ title, subtitle, onExport, disabled, extra, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 truncate flex items-center gap-2">{title}</div>
          {subtitle && <div className="text-[11.5px] text-slate-500 truncate">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {extra}
          {onExport && (
            <button
              onClick={onExport} disabled={disabled}
              data-testid={`section-export-${(typeof title === "string" ? title : "").toLowerCase().replace(/\s+/g, "-")}`}
              className="h-7 px-2 rounded-md text-[11.5px] font-semibold border flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            >
              <FileDown className="h-3.5 w-3.5" /> Export CSV
            </button>
          )}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function LockedBadge({ run }) {
  const ts = run?.lockedAt ? new Date(run.lockedAt).toLocaleDateString() : "";
  return (
    <span
      data-testid="payroll-locked-badge"
      title={`Locked ${ts}${run?.lockedBy ? ` by ${run.lockedBy}` : ""}`}
      className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
    >
      <Lock className="h-3 w-3" /> Locked
    </span>
  );
}

function Stat({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-lg px-3 py-2">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`text-[16px] font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-10 text-[13px] text-slate-400 italic">{text}</div>;
}

// ---------- Reusable clickable list panel (rows open the customer file) ----------
function Pill({ text, cls }) {
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${cls}`}>{text}</span>
  );
}

const INVOICE_STATUS_CLS = {
  Paid: "bg-emerald-50 text-emerald-700",
  Partial: "bg-amber-50 text-amber-700",
  Unpaid: "bg-slate-100 text-slate-600",
  Overdue: "bg-rose-50 text-rose-700",
  Sent: "bg-blue-50 text-blue-700",
  Draft: "bg-slate-100 text-slate-500",
  Voided: "bg-slate-100 text-slate-400 line-through",
};
const METHOD_CLS = {
  card: "bg-indigo-50 text-indigo-700",
  cash: "bg-emerald-50 text-emerald-700",
  check: "bg-sky-50 text-sky-700",
  ach: "bg-violet-50 text-violet-700",
  online: "bg-blue-50 text-blue-700",
  other: "bg-slate-100 text-slate-600",
};

function ListPanel({ title, titleColor, count, columns, rows, rowKey, getCustomerId, onOpenCustomer, emptyText, testid, onExport }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
        {titleColor && <span className="h-2 w-2 rounded-full" style={{ background: titleColor }} />}
        <span className="text-[12px] font-semibold text-slate-700">{title}</span>
        <span className="text-[11px] font-semibold text-slate-400">{count}</span>
        {onExport && rows.length > 0 && (
          <button
            type="button"
            onClick={onExport}
            data-testid={`${testid}-export`}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-blue-600 transition-colors"
            title="Export CSV"
          >
            <FileDown className="h-3.5 w-3.5" /> Export
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-[12.5px] text-slate-400 italic">{emptyText}</div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white border-b border-slate-100 sticky top-0 z-10">
              <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2 ${c.thClass || ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const cid = getCustomerId ? getCustomerId(row) : null;
                const clickable = !!cid;
                return (
                  <tr
                    key={rowKey(row)}
                    onClick={clickable ? () => onOpenCustomer(cid) : undefined}
                    data-testid={`${testid}-row`}
                    className={clickable ? "hover:bg-blue-50/40 cursor-pointer transition-colors" : ""}
                    title={clickable ? "Open customer file" : undefined}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className={`px-3 py-2.5 ${c.tdClass || ""}`}>{c.render(row)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Payroll section (admin-only) ----------
function UnpaidPanel({ rows, fromDate, toDate, onOpenCustomer, onOpenJob, onOpenInvoice }) {
  const total = rows.reduce((s, r) => s + (r.balanceDue || 0), 0);
  const exportCsv = () => {
    const stamp = fromDate || toDate ? `_${fromDate || "start"}_to_${toDate || "today"}` : "_all";
    const data = rows.map((r) => [
      r.jobId || "—", r.invoiceId, r.customer, fmtShort(r.when), fmtShort(r.dueAt || ""),
      r.total.toFixed(2), r.balanceDue.toFixed(2), r.status,
    ]);
    downloadCsv(`invoices-unpaid${stamp}.csv`,
      ["Job #", "Invoice #", "Customer", "Issued", "Due", "Total", "Balance Due", "Status"], data);
    toast.success(`Exported ${data.length} unpaid invoices`);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="jobs-status-unpaid">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
        <span className="h-2 w-2 rounded-full" style={{ background: "#F59E0B" }} />
        <span className="text-[12px] font-semibold text-slate-700">Unpaid</span>
        <span className="text-[11px] font-semibold text-slate-400">{rows.length}</span>
        <span className="ml-2 text-[11px] font-semibold text-amber-700">Balance {money(total)}</span>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={exportCsv}
            data-testid="jobs-status-unpaid-export"
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-blue-600 transition-colors"
            title="Export CSV"
          >
            <FileDown className="h-3.5 w-3.5" /> Export
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-[12.5px] text-slate-400 italic">—</div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white border-b border-slate-100 sticky top-0 z-10">
              <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">
                <th className="px-3 py-2 w-28">Job #</th>
                <th className="px-3 py-2 w-28">Invoice #</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2 w-28">Issued</th>
                <th className="px-3 py-2 w-28">Due</th>
                <th className="px-3 py-2 w-24 text-right">Total</th>
                <th className="px-3 py-2 w-28 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr
                  key={r.invoiceId}
                  data-testid={`jobs-status-unpaid-row-${r.invoiceId}`}
                  className={r.customerId ? "hover:bg-amber-50/40 cursor-pointer transition-colors" : ""}
                  onClick={r.customerId ? () => onOpenCustomer(r.customerId) : undefined}
                  title={r.customerId ? "Open customer file" : undefined}
                >
                  <td className="px-3 py-2.5">
                    {r.jobId ? (
                      <button
                        type="button"
                        data-testid={`unpaid-open-job-${r.jobId}`}
                        onClick={(e) => { e.stopPropagation(); onOpenJob && onOpenJob(r.jobId); }}
                        className="font-semibold text-blue-600 hover:underline"
                      >
                        {r.jobId}
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      data-testid={`unpaid-open-invoice-${r.invoiceId}`}
                      onClick={(e) => { e.stopPropagation(); onOpenInvoice && onOpenInvoice(r.invoiceId); }}
                      className="font-semibold text-blue-600 hover:underline"
                    >
                      {r.invoiceId}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-slate-800 truncate">{r.customer}</td>
                  <td className="px-3 py-2.5 text-slate-500">{fmtShort(r.when)}</td>
                  <td className="px-3 py-2.5 text-slate-500">{r.dueAt ? fmtShort(r.dueAt) : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{money2(r.total)}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-amber-700">{money2(r.balanceDue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PayrollSection({ techs, staffUsers = [], jobs, invoices, pricebook, jobTitles, fromDate, toDate, applyPreset }) {
  // Unified payroll roster: technician records + non-technician staff accounts
  // that have a compensation model set (so their Profile comp drives Payroll).
  const payrollTechs = useMemo(() => {
    const techEmails = new Set(techs.map((t) => (t.email || "").toLowerCase()).filter(Boolean));
    const techNames = new Set(techs.map((t) => (t.name || "").trim().toLowerCase()).filter(Boolean));
    const acctTechs = (staffUsers || [])
      .filter(
        (u) =>
          u.role !== "super_admin" &&
          !techEmails.has((u.email || "").toLowerCase()) &&
          !techNames.has((u.name || "").trim().toLowerCase()) &&
          u.pay && u.pay.model,
      )
      .map((u) => ({
        id: u.id,
        userId: u.id,
        name: u.name || (u.email || "").split("@")[0],
        email: u.email,
        jobTitleId: u.jobTitleId || null,
        pay: u.pay,
        status: "Off",
        isAccount: true,
        performance: { jobsToday: 0, revenueMonth: 0, avgTicket: 0, rating: 0 },
      }));
    return [...techs, ...acctTechs];
  }, [techs, staffUsers]);
  const [overrides, setOverrides] = useState({}); // techId → {bonus, manualHours} (legacy, read-only — corrections now use reasoned Adjustments)
  const [loadingOv, setLoadingOv] = useState(false);
  const [run, setRun] = useState(null); // legacy PayrollRun doc — kept only for email-sent timestamps
  const [emailing, setEmailing] = useState(null);
  const [includedJobs, setIncludedJobs] = useState({}); // techId → { jobId: true } (non-completed jobs to count)
  const [viewCustomerId, setViewCustomerId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null); // techId currently exporting
  const [clockHours, setClockHours] = useState({ byEmail: {}, byUserId: {} }); // clocked hours in range
  const [detailRow, setDetailRow] = useState(null); // employee row shown in the Payroll Detail drawer
  const [workspaceView, setWorkspaceView] = useState("table"); // "table" | "review"
  const [adjustments, setAdjustments] = useState([]); // PayrollAdjustments for this period
  const [taxByEmployee, setTaxByEmployee] = useState({}); // techId → {gross, currentDeductions, net, deductions}
  const [addAdjustmentFor, setAddAdjustmentFor] = useState(undefined); // undefined=closed, null/employeeId=open
  const [timeModalConfig, setTimeModalConfig] = useState(null); // {defaultDate, focusField} or null=closed
  const [commissionEditFor, setCommissionEditFor] = useState(null); // {job, systemAmount, reversal} or null
  const [commissionSplitFor, setCommissionSplitFor] = useState(null); // {job, systemAmount} or null
  const [pieceRateEditFor, setPieceRateEditFor] = useState(null); // {line, systemAmount} or null
  const [addSpiffFor, setAddSpiffFor] = useState(undefined); // {employeeId} or undefined
  const [addHolidayFor, setAddHolidayFor] = useState(undefined); // {employeeId} or undefined
  const [showPeriodAudit, setShowPeriodAudit] = useState(false);
  const [transitionBusy, setTransitionBusy] = useState(false);
  const { isAdminPerm, permissions } = useAuth();
  const canPayroll = useMemo(() => makeCanPayroll(isAdminPerm, permissions), [isAdminPerm, permissions]);
  const titleById = useMemo(() => {
    const m = new Map();
    jobTitles.forEach((t) => m.set(t.id, t.name));
    return m;
  }, [jobTitles]);

  // Default range to current week if not set
  useEffect(() => {
    if (!fromDate && !toDate) applyPreset("this-month"); // user picked Weekly cadence but This-month is more useful as default
  }, []); // eslint-disable-line

  const rangeReady = !!fromDate && !!toDate;

  // Load overrides for this range
  useEffect(() => {
    if (!rangeReady) return;
    let cancelled = false;
    setLoadingOv(true);
    Promise.all([
      payrollApi.listOverrides(fromDate, toDate),
      payrollApi.getRun(fromDate, toDate).catch(() => null),
      timeclockApi.hours(fromDate, toDate).catch(() => ({ byEmail: {}, byUserId: {} })),
    ])
      .then(([rows, runDoc, clocked]) => {
        if (cancelled) return;
        const map = {};
        rows.forEach((r) => { map[r.techId] = { bonus: r.bonus || 0, manualHours: r.manualHours }; });
        setOverrides(map);
        setRun(runDoc || null);
        setClockHours(clocked || { byEmail: {}, byUserId: {} });
        setLoadingOv(false);
      })
      .catch((err) => { console.error(err); setLoadingOv(false); });
    return () => { cancelled = true; };
  }, [fromDate, toDate, rangeReady]);

  const { period, loading: periodLoading, transition: transitionPeriod, patchPayDate } = usePayrollPeriod(fromDate, toDate);
  const isLocked = period?.status === "LOCKED";
  const sentTimestamps = run?.sentTimestamps || {};

  // Load adjustments for this period
  useEffect(() => {
    if (!period?.id) { setAdjustments([]); return; }
    payrollApi.listAdjustments(period.id).then(setAdjustments).catch(() => setAdjustments([]));
  }, [period?.id]);

  // Compute payroll per tech
  const inRange = (iso) => {
    if (!iso) return false;
    const ts = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).getTime();
    const f = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : -Infinity;
    const t = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : Infinity;
    return ts >= f && ts <= t;
  };

  const computeForTech = (tech) => {
    const pay = tech.pay || { model: "hourly", hourlyRate: 0, commissionPct: 0, commissionBase: "job-value", tradeOverrides: [], flatRatePerJob: 0, overtimeRule: "40wk", overtimeMultiplier: 1.5 };
    const techJobs = jobs.filter((j) => j.techId === tech.id && inRange(j.end || j.start));
    const completed = techJobs.filter((j) => j.status === "Completed");
    // Hours from GPS log: arrived → finished/completed durations
    let gpsHours = 0;
    completed.forEach((j) => {
      const log = j.log || [];
      const arr = log.find((l) => l.type === "arrived");
      const done = [...log].reverse().find((l) => l.type === "finished" || l.type === "completed");
      if (arr && done) {
        const dur = (new Date(done.time).getTime() - new Date(arr.time).getTime()) / 3600000;
        if (dur > 0 && dur < 24) gpsHours += dur;
      }
    });
    const ov = overrides[tech.id] || {};
    const clockedHours = Number(
      clockHours.byEmail?.[(tech.email || "").toLowerCase()] ??
      clockHours.byUserId?.[tech.userId] ??
      0,
    ) || 0;
    // Hours source of truth: manual override → clocked hours → GPS-derived fallback.
    const totalHours =
      (ov.manualHours !== null && ov.manualHours !== undefined && !Number.isNaN(ov.manualHours))
        ? Number(ov.manualHours)
        : (clockedHours > 0 ? clockedHours : gpsHours);

    // OT split: 40hrs/week threshold * weeks in range. Simple model.
    let regularHours = totalHours, otHours = 0;
    if ((pay.model === "hourly" || pay.model === "hybrid") && pay.overtimeRule !== "none") {
      const days = (new Date(`${toDate}T00:00:00`).getTime() - new Date(`${fromDate}T00:00:00`).getTime()) / 86400000 + 1;
      const weeks = Math.max(1, Math.ceil(days / 7));
      const cap = 40 * weeks;
      regularHours = Math.min(totalHours, cap);
      otHours = Math.max(0, totalHours - cap);
    }
    const regularPay = (pay.model === "hourly" || pay.model === "hybrid") ? regularHours * (pay.hourlyRate || 0) : 0;
    const otPay = (pay.model === "hourly" || pay.model === "hybrid") ? otHours * (pay.hourlyRate || 0) * (pay.overtimeMultiplier || 1.5) : 0;

    // Per-job commission breakdown (Step 11). Completed jobs always count;
    // non-completed jobs count only when the admin toggles "Include".
    const includeSet = includedJobs[tech.id] || {};
    const isCounted = (j) => j.status === "Completed" || !!includeSet[j.id];
    const invoiceAmountFor = (j) => {
      const inv = invoices.find((iv) => iv.jobId === j.id);
      return inv ? Number(inv.total) || 0 : Number(j.value) || 0;
    };
    const commissionFor = (j) => {
      if (pay.model !== "commission" && pay.model !== "hybrid") return { pct: 0, amount: 0 };
      const override = (pay.tradeOverrides || []).find((o) => o.trade === j.trade);
      const pct = (override ? override.pct : pay.commissionPct) || 0;
      let base = 0;
      if (pay.commissionBase === "invoice-paid") {
        invoices.forEach((inv) => {
          if (inv.jobId !== j.id) return;
          (inv.payments || []).forEach((p) => { if (inRange(p.receivedAt)) base += Number(p.amount) || 0; });
        });
      } else if (pay.commissionBase === "profit") {
        const inv = invoices.find((iv) => iv.jobId === j.id);
        const matCost = inv ? (inv.lineItems || []).reduce((s, li) => {
          const pb = pricebook.find((p) => p.id === li.pricebookId);
          if (pb && pb.kind === "material") return s + (Number(li.qty) || 0) * (Number(pb.cost) || 0);
          return s;
        }, 0) : 0;
        base = (Number(j.value) || 0) - matCost;
      } else {
        base = Number(j.value) || 0;
      }
      return { pct, amount: base * (pct / 100) };
    };

    const jobLines = techJobs
      .map((j) => {
        const c = commissionFor(j);
        return {
          jobId: j.id, customer: j.customer, customerId: j.customerId,
          type: j.trade || j.type || "—", status: j.status,
          invoiceAmount: invoiceAmountFor(j),
          commissionPct: c.pct, commissionAmount: c.amount,
          isCompleted: j.status === "Completed", included: isCounted(j),
        };
      })
      .sort((a, b) => (a.isCompleted === b.isCompleted ? 0 : a.isCompleted ? -1 : 1));

    let commission = 0;
    jobLines.forEach((jl) => { if (jl.included) commission += jl.commissionAmount; });

    // Piece-rate breakdown: completed jobs' invoice line items tagged with a
    // pricebook item that has a pieceRate set (Service/Product × Qty × Rate).
    const pieceRateLines = [];
    if (pay.model === "piece_rate") {
      completed.forEach((j) => {
        const inv = invoices.find((iv) => iv.jobId === j.id);
        (inv?.lineItems || []).forEach((li) => {
          const pb = pricebook.find((p) => p.id === li.pricebookId);
          if (!pb || pb.pieceRate === null || pb.pieceRate === undefined) return;
          const qty = Number(li.qty) || 0;
          const rate = Number(pb.pieceRate) || 0;
          pieceRateLines.push({
            jobId: j.id, customer: j.customer, customerId: j.customerId, status: j.status,
            pricebookId: pb.id, product: pb.name, qty, rate, total: Math.round(qty * rate * 100) / 100,
          });
        });
      });
    }
    const pieceRateTotal = pieceRateLines.reduce((s, l) => s + l.total, 0);

    // Flat per job
    const flatPay = pay.model === "flat" ? completed.length * (pay.flatRatePerJob || 0) : pay.model === "piece_rate" ? pieceRateTotal : 0;
    const salaryPay = pay.model === "salary" ? salaryPerPeriod(pay.annualSalary, pay.payFrequency) : 0;
    const bonus = Number(ov.bonus) || 0;
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    // Round each money component to cents so the displayed row breakdown sums
    // exactly to the row total (and the grand total = Σ rounded row totals).
    const rRegular = r2(regularPay), rOt = r2(otPay), rCommission = r2(commission), rFlat = r2(flatPay), rBonus = r2(bonus), rSalary = r2(salaryPay);
    const total = r2(rRegular + rOt + rCommission + rFlat + rBonus + rSalary);
    return {
      tech, pay,
      jobsCount: completed.length,
      gpsHours: Math.round(gpsHours * 100) / 100,
      clockedHours: Math.round(clockedHours * 100) / 100,
      hoursUsed: Math.round(totalHours * 100) / 100,
      hoursOverridden: ov.manualHours !== null && ov.manualHours !== undefined,
      hoursSource: (ov.manualHours !== null && ov.manualHours !== undefined) ? "manual" : (clockedHours > 0 ? "clocked" : "gps"),
      regularHours: Math.round(regularHours * 100) / 100,
      otHours: Math.round(otHours * 100) / 100,
      regularPay: rRegular, otPay: rOt, commission: rCommission, flatPay: rFlat, salaryPay: rSalary, bonus: rBonus, total,
      jobLines, pieceRateLines,
    };
  };

  const rows = useMemo(() => payrollTechs.map(computeForTech), [payrollTechs, jobs, invoices, pricebook, overrides, includedJobs, fromDate, toDate, clockHours]); // eslint-disable-line react-hooks/exhaustive-deps
  const grandTotal = Math.round(rows.reduce((s, r) => s + r.total, 0) * 100) / 100;
  const invoiceByJobId = useMemo(() => {
    const m = new Map();
    (invoices || []).forEach((iv) => { if (iv.jobId) m.set(iv.jobId, iv); });
    return m;
  }, [invoices]);

  const toggleInclude = (techId, jobId) =>
    setIncludedJobs((m) => {
      const cur = { ...(m[techId] || {}) };
      if (cur[jobId]) delete cur[jobId]; else cur[jobId] = true;
      return { ...m, [techId]: cur };
    });

  // Build the row payload (with per-job lines) sent to the pay-stub PDF/email.
  const buildRowPayload = (r) => ({
    techName: r.tech.name,
    jobTitle: titleById.get(r.tech.jobTitleId) || "",
    model: r.pay.model,
    jobsCount: r.jobsCount,
    hoursUsed: r.hoursUsed,
    regularHours: r.regularHours,
    otHours: r.otHours,
    regularPay: r.regularPay,
    otPay: r.otPay,
    commission: r.commission,
    flatPay: r.flatPay,
    salaryPay: r.salaryPay,
    bonus: r.bonus,
    total: r.total,
    jobLines: r.jobLines.map((jl) => ({
      jobId: jl.jobId, customer: jl.customer, type: jl.type, status: jl.status,
      invoiceAmount: jl.invoiceAmount, commissionPct: jl.commissionPct,
      commissionAmount: jl.commissionAmount, included: jl.included,
    })),
  });

  const savePdf = async (r) => {
    setPdfBusy(r.tech.id);
    try {
      await payrollApi.downloadPaystub(r.tech.id, fromDate, toDate, { row: buildRowPayload(r) });
      toast.success(`Pay stub PDF for ${r.tech.name} downloaded`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "PDF export failed");
    } finally {
      setPdfBusy(null);
    }
  };

  const exportCsv = () => {
    const headers = ["Tech ID", "Tech", "Job title", "Model", "Jobs", "GPS hours", "Hours used", "Regular pay", "OT pay", "Commission", "Flat", "Bonus", "Adjustments", "Taxes", "Net"];
    const data = rows.map((r) => {
      const adj = adjustmentsTotalByEmployee[r.tech.id] || 0;
      const tax = taxByEmployee[r.tech.id];
      const net = r.total + adj - (tax?.currentDeductions || 0);
      return [
        r.tech.id, r.tech.name, titleById.get(r.tech.jobTitleId) || "",
        r.pay.model, r.jobsCount, r.gpsHours, r.hoursUsed,
        r.regularPay.toFixed(2), r.otPay.toFixed(2), r.commission.toFixed(2),
        r.flatPay.toFixed(2), r.bonus.toFixed(2), adj.toFixed(2),
        (tax?.currentDeductions || 0).toFixed(2), net.toFixed(2),
      ];
    });
    const stamp = `_${fromDate}_to_${toDate}`;
    downloadCsv(`payroll${stamp}.csv`, headers, data);
    toast.success(`Exported ${data.length} rows`);
  };

  const adjustmentsByEmployee = useMemo(() => {
    const m = {};
    adjustments.forEach((a) => { (m[a.employeeId] = m[a.employeeId] || []).push(a); });
    return m;
  }, [adjustments]);

  const adjustmentsTotalByEmployee = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.tech.id] = adjustmentDollarTotal(adjustmentsByEmployee[r.tech.id], r); });
    return m;
  }, [rows, adjustmentsByEmployee]);

  const adjustmentsGrandTotal = useMemo(
    () => Object.values(adjustmentsTotalByEmployee).reduce((s, v) => s + v, 0),
    [adjustmentsTotalByEmployee],
  );

  // Tax estimate reuses the existing FICA + configured Fed/State engine (services/paystub.py)
  // via a persist=False preview call — no tax logic is duplicated here.
  useEffect(() => {
    if (!rangeReady || rows.length === 0) { setTaxByEmployee({}); return; }
    let cancelled = false;
    const payloadRows = rows.map((r) => ({
      techId: r.tech.id,
      regularHours: r.regularHours, otHours: r.otHours,
      regularPay: r.regularPay, otPay: r.otPay,
      commission: r.commission, flatPay: r.flatPay, bonus: r.bonus,
      total: r.total + (adjustmentsTotalByEmployee[r.tech.id] || 0),
    }));
    payrollApi.taxPreview(fromDate, toDate, payloadRows)
      .then((res) => {
        if (cancelled) return;
        const m = {};
        (res.results || []).forEach((x) => { m[x.techId] = x; });
        setTaxByEmployee(m);
      })
      .catch((err) => console.error(err));
    return () => { cancelled = true; };
  }, [rows, adjustmentsTotalByEmployee, fromDate, toDate, rangeReady]);

  const taxGrandTotals = useMemo(() => {
    const t = { gross: 0, currentDeductions: 0, net: 0 };
    Object.values(taxByEmployee).forEach((x) => {
      t.gross += x.gross || 0; t.currentDeductions += x.currentDeductions || 0; t.net += x.net || 0;
    });
    return t;
  }, [taxByEmployee]);

  const handleAddAdjustment = async (payload) => {
    if (!period?.id) throw new Error("Payroll period not ready yet");
    const created = await payrollApi.createAdjustment(period.id, payload);
    setAdjustments((prev) => [created, ...prev]);
    toast.success("Adjustment saved");
  };

  const handleApproveAdjustment = async (adjustmentId, approve) => {
    try {
      const updated = await payrollApi.updateAdjustment(adjustmentId, { approve });
      setAdjustments((prev) => prev.map((a) => (a.id === adjustmentId ? updated : a)));
      toast.success(approve ? "Adjustment marked approved" : "Adjustment marked pending");
    } catch (err) {
      toast.error(errMsg(err, "Failed to update adjustment"));
    }
  };

  const createAdjustmentBatch = async (entries) => {
    if (!period?.id) throw new Error("Payroll period not ready yet");
    const created = [];
    for (const entry of entries) created.push(await payrollApi.createAdjustment(period.id, entry));
    setAdjustments((prev) => [...created, ...prev]);
    return created;
  };

  const handleAddTimeCorrection = async (entries) => {
    const created = await createAdjustmentBatch(entries);
    toast.success(`Saved ${created.length} time correction${created.length > 1 ? "s" : ""}`);
  };

  const handleCommissionSplit = async (entries) => {
    await createAdjustmentBatch(entries);
    toast.success("Commission split saved");
  };

  const handleTransition = async (toStatus, extra = {}) => {
    setTransitionBusy(true);
    try {
      const body = toStatus === "REVIEW" ? { ...extra, snapshotRows: buildSnapshotRows(rows, titleById) } : extra;
      await transitionPeriod(toStatus, body);
      toast.success(`Payroll period → ${toStatus}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not update payroll period"));
    } finally {
      setTransitionBusy(false);
    }
  };

  if (!rangeReady) {
    return <SectionCard title="Payroll" subtitle="Select a date range to compute payroll" disabled><Empty text="Set a From/To range above." /></SectionCard>;
  }

  return (
    <div className="space-y-4" data-testid="payroll-workspace">
      <PayrollPeriodHeader
        period={period}
        fromDate={fromDate}
        toDate={toDate}
        canPayroll={canPayroll}
        busy={transitionBusy || periodLoading}
        onTransition={handleTransition}
        onPatchPayDate={patchPayDate}
        onAddAdjustment={() => setAddAdjustmentFor(null)}
        onExport={exportCsv}
        onShowAudit={() => setShowPeriodAudit(true)}
      />
      <PayrollSummaryCards rows={rows} adjustmentsTotal={adjustmentsGrandTotal} taxTotals={taxGrandTotals} />
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="payroll-view-toggle">
        <button
          onClick={() => setWorkspaceView("table")}
          data-testid="payroll-view-table-btn"
          className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${workspaceView === "table" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
          Employee Table
        </button>
        <button
          onClick={() => setWorkspaceView("review")}
          data-testid="payroll-view-review-btn"
          className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${workspaceView === "review" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
        >
          Review Dashboard
        </button>
      </div>
      {workspaceView === "table" ? (
        <PayrollEmployeeTable
          rows={rows}
          adjustmentsTotalByEmployee={adjustmentsTotalByEmployee}
          taxByEmployee={taxByEmployee}
          onOpenDetail={setDetailRow}
        />
      ) : (
        <PayrollReviewDashboard
          rows={rows}
          adjustmentsByEmployee={adjustmentsByEmployee}
          invoiceByJobId={invoiceByJobId}
          clockHours={clockHours}
          period={period}
          canPayroll={canPayroll}
          onOpenDetail={setDetailRow}
          onApproveAdjustment={handleApproveAdjustment}
        />
      )}
      <div className="text-[11px] text-slate-500">
        Corrections now go through reasoned Adjustments (header's Add Adjustment, or a row's detail view) instead of free-form edits — every change is reason-required and audit-logged.
      </div>

      {detailRow && (
        <PayrollDetailDrawer
          row={detailRow}
          period={period}
          adjustments={adjustmentsByEmployee[detailRow.tech.id] || []}
          taxInfo={taxByEmployee[detailRow.tech.id]}
          canPayroll={canPayroll}
          onClose={() => setDetailRow(null)}
          onAddAdjustment={({ employeeId }) => setAddAdjustmentFor(employeeId)}
          onAddSpiff={({ employeeId }) => setAddSpiffFor({ employeeId })}
          onAddHoliday={({ employeeId }) => setAddHolidayFor({ employeeId })}
          onApproveAdjustment={handleApproveAdjustment}
          onOpenTimeModal={(cfg) => setTimeModalConfig(cfg || {})}
          onSavePdf={() => savePdf(detailRow)}
          onEmail={() => setEmailing(detailRow)}
          pdfBusy={pdfBusy === detailRow.tech.id}
          commissionsSlot={
            <CommissionsTab
              row={detailRow}
              invoices={invoices}
              jobs={jobs}
              pricebook={pricebook}
              adjustments={adjustmentsByEmployee[detailRow.tech.id] || []}
              canPayroll={canPayroll}
              isLocked={isLocked || period?.status !== "OPEN"}
              onToggleInclude={(jobId) => toggleInclude(detailRow.tech.id, jobId)}
              onViewCustomer={(cid) => setViewCustomerId(cid)}
              onEditCommission={(job, systemAmount, reversal) => setCommissionEditFor({ job, systemAmount, reversal })}
              onSplitCommission={(job, systemAmount) => setCommissionSplitFor({ job, systemAmount })}
            />
          }
          pieceRateSlot={
            <PieceRateTab
              row={detailRow}
              adjustments={adjustmentsByEmployee[detailRow.tech.id] || []}
              canPayroll={canPayroll}
              isLocked={isLocked || period?.status !== "OPEN"}
              onViewCustomer={(cid) => setViewCustomerId(cid)}
              onEditPieceRate={(line, systemAmount) => setPieceRateEditFor({ line, systemAmount })}
            />
          }
        />
      )}

      {addAdjustmentFor !== undefined && (
        <AddAdjustmentModal
          employeeId={addAdjustmentFor}
          employees={payrollTechs.map((t) => ({ id: t.id, name: t.name }))}
          onClose={() => setAddAdjustmentFor(undefined)}
          onSubmit={handleAddAdjustment}
        />
      )}

      {timeModalConfig && detailRow && (
        <EditPayrollTimeModal
          employeeId={detailRow.tech.id}
          employeeName={detailRow.tech.name}
          defaultDate={timeModalConfig.defaultDate}
          focusField={timeModalConfig.focusField}
          hourlyRate={(detailRow.pay.model === "hourly" || detailRow.pay.model === "hybrid") ? (detailRow.pay.hourlyRate || 0) : 0}
          onClose={() => setTimeModalConfig(null)}
          onSubmit={handleAddTimeCorrection}
        />
      )}

      {commissionEditFor && detailRow && (
        <CommissionAdjustmentModal
          employeeId={detailRow.tech.id}
          employeeName={detailRow.tech.name}
          job={commissionEditFor.job}
          systemAmount={commissionEditFor.systemAmount}
          reversal={commissionEditFor.reversal}
          onClose={() => setCommissionEditFor(null)}
          onSubmit={handleAddAdjustment}
        />
      )}

      {commissionSplitFor && detailRow && (
        <CommissionSplitModal
          employeeId={detailRow.tech.id}
          employeeName={detailRow.tech.name}
          job={commissionSplitFor.job}
          systemAmount={commissionSplitFor.systemAmount}
          otherTechs={payrollTechs.filter((t) => t.id !== detailRow.tech.id).map((t) => ({ id: t.id, name: t.name }))}
          onClose={() => setCommissionSplitFor(null)}
          onSubmit={handleCommissionSplit}
        />
      )}

      {pieceRateEditFor && detailRow && (
        <PieceRateAdjustmentModal
          employeeId={detailRow.tech.id}
          employeeName={detailRow.tech.name}
          line={pieceRateEditFor.line}
          systemAmount={pieceRateEditFor.systemAmount}
          onClose={() => setPieceRateEditFor(null)}
          onSubmit={handleAddAdjustment}
        />
      )}

      {addSpiffFor && detailRow && (
        <AddSpiffModal
          employeeId={addSpiffFor.employeeId}
          employeeName={detailRow.tech.name}
          jobOptions={detailRow.jobLines}
          onClose={() => setAddSpiffFor(undefined)}
          onSubmit={handleAddAdjustment}
        />
      )}

      {addHolidayFor && detailRow && (
        <AddHolidayPayModal
          employeeId={addHolidayFor.employeeId}
          employeeName={detailRow.tech.name}
          defaultRate={(detailRow.pay.model === "hourly" || detailRow.pay.model === "hybrid") ? (detailRow.pay.hourlyRate || 0) : 0}
          onClose={() => setAddHolidayFor(undefined)}
          onSubmit={handleAddAdjustment}
        />
      )}

      {showPeriodAudit && <PeriodAuditModal periodId={period?.id} onClose={() => setShowPeriodAudit(false)} />}

      {emailing && (
        <PayStubEmailModal
          row={emailing}
          fromDate={fromDate}
          toDate={toDate}
          jobTitleName={titleById.get(emailing.tech.jobTitleId) || ""}
          onClose={() => setEmailing(null)}
        />
      )}
      {viewCustomerId && (
        <CustomerProfileOverlay customerId={viewCustomerId} onClose={() => setViewCustomerId(null)} />
      )}
    </div>
  );
}

// ---------- Pay stub email modal ----------
function PayStubEmailModal({ row, fromDate, toDate, jobTitleName, onClose }) {
  const [to, setTo] = useState(row.tech.email || "");
  const [subject, setSubject] = useState(`Pay stub — ${fromDate} to ${toDate}`);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

  const send = async () => {
    if (!validEmail(to)) {
      toast.error("Please enter a valid recipient email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        to: to.trim(),
        subject: subject.trim() || undefined,
        body: body.trim() || undefined,
        row: {
          techName: row.tech.name,
          jobTitle: jobTitleName,
          model: row.pay.model,
          jobsCount: row.jobsCount,
          hoursUsed: row.hoursUsed,
          regularHours: row.regularHours,
          otHours: row.otHours,
          regularPay: row.regularPay,
          otPay: row.otPay,
          commission: row.commission,
          flatPay: row.flatPay,
          bonus: row.bonus,
          total: row.total,
          jobLines: (row.jobLines || []).map((jl) => ({
            jobId: jl.jobId, customer: jl.customer, type: jl.type, status: jl.status,
            invoiceAmount: jl.invoiceAmount, commissionPct: jl.commissionPct,
            commissionAmount: jl.commissionAmount, included: jl.included,
          })),
        },
      };
      const res = await payrollApi.emailPaystub(row.tech.id, fromDate, toDate, payload);
      if (res.sent) {
        toast.success(`Pay stub sent to ${to.trim()}`);
        onClose();
      } else if (res.fallback) {
        toast.warning(`Email saved to dev log (Resend not configured or send failed). Reason: ${res.reason || "unknown"}`);
        onClose();
      } else {
        toast.error(res.reason || "Email failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Email failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200" data-testid="paystub-email-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">Email pay stub to {row.tech.name}</div>
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-[12.5px] text-emerald-900 flex items-center justify-between">
            <span><strong>{row.tech.name}</strong> · {fromDate} → {toDate}</span>
            <span className="font-bold text-[14px]">{money2(row.total)}</span>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">To</label>
            <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="employee@example.com"
              data-testid="paystub-email-to"
              className="w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
            {!row.tech.email && (
              <div className="mt-1 text-[11px] text-amber-700">No email on file for {row.tech.name}. Enter one above.</div>
            )}
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Message (optional)</label>
            <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Hi ${row.tech.name},\n\nPlease find your pay stub attached.`}
              className="w-full px-2.5 py-2 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none" />
            <div className="mt-1 text-[11px] text-slate-500">A PDF pay stub will be attached automatically.</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 hover:bg-white text-[13px] font-semibold">Cancel</button>
          <button
            onClick={send}
            disabled={submitting || !validEmail(to)}
            data-testid="paystub-email-send"
            className="h-9 px-3 rounded-md bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold flex items-center"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4 mr-1.5" /> Send Pay Stub</>}
          </button>
        </div>
      </div>
    </div>
  );
}
